import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Award, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useBreadcrumbLabel } from "@/contexts/BreadcrumbContext";

function CourseCard({ c, idx, onEnroll }) {
    return (
        <article
            className="group bg-white rounded-2xl overflow-hidden shadow-soft border border-gray-100 transition-all duration-300 hover:shadow-brand hover:-translate-y-1 animate-fade-up"
            style={{ animationDelay: `${idx * 60}ms` }}
            data-testid={`course-card-${c.id}`}
        >
            <div className="relative aspect-[4/3] overflow-hidden">
                <img
                    src={c.image_url}
                    alt={c.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-[11px] font-semibold uppercase tracking-wider text-brand-700">
                    {c.level}
                </span>
            </div>
            <div className="p-6">
                <Link to={`/courses/${c.id}`} className="block hover:text-brand-600 transition-colors" data-testid={`course-link-${c.id}`}>
                    <h3 className="font-heading text-xl font-semibold text-ink-900 hover:text-brand-600 transition-colors">
                        {c.title}
                    </h3>
                </Link>
                <p className="mt-2 text-sm text-ink-700 leading-relaxed line-clamp-3">
                    {c.description}
                </p>
                <div className="mt-4 flex flex-wrap gap-3 text-xs text-ink-700">
                    <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-brand-500" />
                        {c.duration}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Award className="h-3.5 w-3.5 text-accent-500" />
                        Certificate
                    </span>
                </div>
                <div className="mt-6 flex items-end justify-between gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider text-ink-400">From</div>
                        <div className="font-heading text-2xl font-semibold text-ink-900">
                            ₹{c.price.toLocaleString("en-IN")}
                        </div>
                        <div className="text-[11px] text-ink-700 mt-0.5">
                            +₹{(c.offline_surcharge ?? 1500).toLocaleString("en-IN")} for offline
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                        <Button
                            onClick={() => onEnroll(c, "online")}
                            className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-4 h-9 text-sm"
                            data-testid={`enroll-online-${c.id}`}
                        >
                            Enroll Online
                        </Button>
                        <Button
                            onClick={() => onEnroll(c, "offline")}
                            disabled={(c.offline_seats_remaining ?? 1) <= 0}
                            variant="outline"
                            className="rounded-full border-2 border-accent-400 text-accent-600 hover:bg-accent-50 hover:text-accent-700 hover:border-accent-500 px-4 h-9 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`enroll-offline-${c.id}`}
                        >
                            {(c.offline_seats_remaining ?? 1) <= 0 ? "Sold Out" : "Enroll Offline"}
                        </Button>
                    </div>
                </div>
                <Link to={`/courses/${c.id}`} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700" data-testid={`course-view-details-${c.id}`}>
                    View details <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            </div>
        </article>
    );
}

export default function CourseCategoryPage({ onOpenAuth }) {
    const { slug } = useParams();
    const [cats, setCats] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const { addItem } = useCart();

    useEffect(() => {
        Promise.all([
            api.get("/course-categories").then((r) => r.data).catch(() => []),
            api.get("/courses").then((r) => r.data).catch(() => []),
        ]).then(([cs, courseList]) => {
            setCats(cs || []);
            setCourses(courseList || []);
            setLoading(false);
        });
    }, []);

    const category = useMemo(() => cats.find((c) => c.slug === slug) || null, [cats, slug]);
    useBreadcrumbLabel(`/course-categories/${slug}`, category?.name || null);
    const filtered = useMemo(() => {
        if (!category) return [];
        return courses.filter((c) => c.category_id === category.id);
    }, [courses, category]);

    const handleEnroll = (c, mode = "online") => {
        const surcharge = mode === "offline" ? (c.offline_surcharge ?? 1500) : 0;
        const price = c.price + surcharge;
        addItem(
            { id: c.id, kind: "course", name: c.title, price, image_url: c.image_url, meta: c.duration, mode },
            1,
        );
        toast.success(`Enrolled (${mode === "offline" ? "Offline · Studio" : "Online"})`, {
            description: `${c.title} added to your cart.`,
        });
    };

    return (
        <div className="min-h-screen bg-paper">
            <Navbar onOpenAuth={onOpenAuth} />
            <main>
                {/* Banner */}
                <section className="relative overflow-hidden" data-testid="category-banner">
                    <div className="relative h-[280px] md:h-[380px] bg-gray-200">
                        {category?.feature_image ? (
                            <img src={category.feature_image} alt={category.name} className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-brand-100 to-accent-100" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/30 to-black/10" />
                        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-10">
                            <Link to="/#courses" className="inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-white mb-3" data-testid="category-back-link">
                                <ArrowLeft className="h-4 w-4" /> All categories
                            </Link>
                            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight" data-testid="category-title">
                                {loading ? "Loading…" : (category ? category.name : "Category not found")}
                            </h1>
                            {category?.description && (
                                <p className="mt-3 max-w-3xl text-base md:text-lg text-white/90" data-testid="category-description">
                                    {category.description}
                                </p>
                            )}
                        </div>
                    </div>
                </section>

                <section className="py-16 md:py-20" data-testid="category-courses-section">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {loading ? (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {[0, 1, 2].map((i) => (
                                    <div key={i} className="h-96 rounded-2xl bg-gray-100 animate-pulse" />
                                ))}
                            </div>
                        ) : !category ? (
                            <div className="text-center py-16">
                                <p className="text-ink-700">We couldn't find this category.</p>
                                <Link to="/#courses" className="mt-4 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                                    Browse all categories <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        ) : filtered.length === 0 ? (
                            <p className="text-center py-16 text-ink-700" data-testid="category-empty">
                                No courses in this category yet — check back soon.
                            </p>
                        ) : (
                            <>
                                <div className="mb-8 text-sm text-ink-700">
                                    Showing <span className="font-semibold text-ink-900">{filtered.length}</span> course{filtered.length === 1 ? "" : "s"} in <span className="font-semibold text-ink-900">{category.name}</span>
                                </div>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="category-courses-grid">
                                    {filtered.map((c, i) => (
                                        <CourseCard key={c.id} c={c} idx={i} onEnroll={handleEnroll} />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
